import {
  queryKeys,
  useHttpClient,
  useActiveOrganizationId,
} from "@repo/api-client/react";
import {
  ServiceSchema,
  type Service,
  type UpdateServiceRequest,
} from "@repo/api-client/dtos";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebouncedAsyncCallback } from "../internal/useDebouncedAsyncCallback";
import { patchJSON } from "../internal/http";

export type UpdateServiceVariables = {
  serviceId: string;
  updates: UpdateServiceRequest;
};

export function useUpdateService() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: ({ serviceId, updates }: UpdateServiceVariables) => {
      if (!orgId) throw new Error("No active organization");
      return patchJSON(
        http,
        `/orgs/${orgId}/services/${serviceId}`,
        updates,
        ServiceSchema,
      );
    },
    onMutate: async ({ serviceId, updates }) => {
      if (!orgId) return { previous: undefined };
      const detailKey = queryKeys.items.detail(orgId, serviceId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<Service>(detailKey);
      if (previous) {
        queryClient.setQueryData<Service>(detailKey, {
          ...previous,
          ...updates,
        });
      }
      return { previous };
    },
    onError: (_err, { serviceId }, context) => {
      if (orgId && context?.previous) {
        queryClient.setQueryData(
          queryKeys.items.detail(orgId, serviceId),
          context.previous,
        );
      }
    },
    onSuccess: (service) => {
      queryClient.setQueryData(
        queryKeys.items.detail(service.orgId, service.id),
        service,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.all(service.orgId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.items.activities(service.orgId, service.id),
      });
    },
  });

  return {
    updateService: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Debounced wrapper around `updateService`. Returns
 * `{ call, flush, retry, pending, error, succeededAt }` — see
 * `useDebouncedUpdateOrganization`
 * for the contract and Phase 6 / plan.md H1 for the rationale.
 */
export function useDebouncedUpdateService(delayMs = 1000) {
  const { updateService } = useUpdateService();
  return useDebouncedAsyncCallback(
    (vars: UpdateServiceVariables) => updateService(vars),
    delayMs,
  );
}
